<template>
  <div class="page-container bg-surface-50 dark:bg-surface-950">
    <Navbar />

    <!-- Loading State -->
    <LoadingSpinner v-if="isLoading" is-full-page message="Loading dashboard data..." />

    <!-- Main Content -->
    <main v-else class="page-content">
      <div v-if="userProfile" class="space-y-8">
        <!-- Header Section -->
        <div class="page-header animate-fade-up">
          <div class="flex items-start justify-between">
            <div>
              <Breadcrumbs :items="breadcrumbItems" />
              <h1 class="text-display-sm text-surface-900 dark:text-surface-50 mb-1 mt-3">
                Dashboard
              </h1>
              <p class="text-surface-500 dark:text-surface-400 text-sm">
                Fleet consumption overview &amp; environmental impact
              </p>
            </div>
            <img
              v-if="userProfile.customer_logo && userProfile.customer_logo !== 'URL'"
              :src="userProfile.customer_logo"
              alt="Customer Logo"
              class="h-20 w-20 object-contain rounded-2xl shadow-card border border-surface-200/60 dark:border-surface-800/60 bg-white dark:bg-surface-900/80"
            />
          </div>
        </div>

        <!-- Filters Section -->
        <div class="animate-fade-up" style="animation-delay: 40ms">
          <BaseCard class="sticky top-4 z-10 border border-surface-200/60 dark:border-surface-700/60 backdrop-blur-sm bg-white/80 dark:bg-surface-900/80 rounded-2xl">
            <template #header>
              <div class="flex items-center gap-3">
                <div class="p-1.5 rounded-lg bg-accent-500/10">
                  <AdjustmentsHorizontalIcon class="h-5 w-5 text-accent-500" />
                </div>
                <div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-100">Filters</h3>
                  <p class="text-[10px] text-surface-400 dark:text-surface-500">Refine your dashboard view</p>
                </div>
              </div>
            </template>
            <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div class="md:col-span-2">
                <DateRangePicker v-model="dateRange" :min="minDate" :max="maxDate" />
              </div>
              <EntityFilter
                v-model="selectedEntities"
                :options="allEntities"
                :label="userProfile.group ? 'Vessel Filter' : 'Asset Filter'"
                :placeholder="userProfile.group ? 'Select Vessels' : 'Select Assets'"
              />
              <EntityFilter
                v-model="selectedSites"
                :options="allSites"
                label="Site Filter"
                placeholder="Select Sites"
              />
            </div>
          </BaseCard>
        </div>

        <PortalAdminCustomerPicker
          v-if="isPortalSuperAdmin"
          class="animate-fade-up"
          style="animation-delay: 40ms"
        />

        <!-- Empty-state: account not yet configured in MCS -->
        <template v-if="customersMcs.length === 0">
          <div class="animate-fade-up" style="animation-delay: 60ms">
            <EmptyState
              :icon="AdjustmentsHorizontalIcon"
              :title="isPortalSuperAdmin ? 'No customer selected' : 'Account setup in progress'"
              :message="isPortalSuperAdmin
                ? 'Use the picker above to choose which customer\'s portal to view.'
                : 'Your account is being set up. Please contact your administrator.'"
              variant="info"
            />
          </div>
        </template>

        <template v-else>
        <!-- Account Tabs -->
        <div v-if="customersMcs.length > 1" class="animate-fade-up" style="animation-delay: 60ms">
          <div class="border-b border-surface-200 dark:border-surface-700">
            <nav class="-mb-px flex space-x-1 overflow-x-auto scrollbar-hide" aria-label="Tabs">
              <button
                v-for="(customer, index) in customersMcs"
                :key="customer.mcs_customer_id"
                @click="transactionsIndex = index"
                :class="[
                  transactionsIndex === index
                    ? 'border-accent-500 text-accent-600 dark:text-accent-400'
                    : 'border-transparent text-surface-500 hover:text-surface-700 hover:border-surface-300 dark:text-surface-400 dark:hover:text-surface-300',
                  'whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm transition-colors duration-200'
                ]"
              >
                {{ customer.customer_name }} &mdash; {{ customer.site }}
              </button>
            </nav>
          </div>
        </div>

        <!-- Selected Account Display -->
        <div v-if="customersMcs[transactionsIndex]" class="space-y-6">
          <!-- Account Header -->
          <div class="animate-fade-up" style="animation-delay: 80ms">
            <div class="flex items-center gap-2 mb-4">
              <div class="h-px flex-1 bg-gradient-to-r from-accent-500/40 to-transparent"></div>
              <span class="text-xs font-bold uppercase tracking-[0.2em] text-accent-600 dark:text-accent-400">Fleet Overview</span>
              <div class="h-px flex-1 bg-gradient-to-l from-accent-500/40 to-transparent"></div>
            </div>
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-xl font-bold text-surface-900 dark:text-surface-50 tracking-tight">
                  {{ customersMcs[transactionsIndex].customer_name }}
                </h2>
                <p class="text-sm text-surface-500 dark:text-surface-400 mt-0.5">
                  {{ customersMcs[transactionsIndex].site }}
                </p>
              </div>
            </div>
          </div>

          <!-- Quick Stats -->
          <div v-if="currentAccountStats" class="animate-fade-up" style="animation-delay: 100ms">
            <div :class="statsGridClass">
              <div
                v-for="(stat, index) in currentAccountStats"
                :key="index"
                class="kpi-card group"
                :style="{ animationDelay: `${100 + index * 50}ms` }"
              >
                <div
                  class="absolute left-0 top-0 bottom-0 w-1 rounded-full transition-all duration-300 group-hover:shadow-lg"
                  :style="{ backgroundColor: stat.color }"
                ></div>
                <div class="flex items-center justify-between pl-3">
                  <div class="flex-1 min-w-0">
                    <p class="text-[10px] font-bold text-surface-400 dark:text-surface-500 uppercase tracking-[0.12em] mb-1.5 leading-tight">
                      {{ stat.label }}
                    </p>
                    <p class="text-xl font-extrabold font-mono text-surface-900 dark:text-surface-50 tabular-nums leading-none break-words">
                      {{ stat.value }}
                    </p>
                  </div>
                  <div
                    v-if="stat.icon"
                    class="ml-3 flex-shrink-0 p-2 rounded-xl transition-all duration-300 group-hover:scale-110"
                    :style="{ backgroundColor: stat.color + '14' }"
                  >
                    <component
                      :is="stat.icon"
                      class="h-5 w-5"
                      :style="{ color: stat.color }"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Environmental Impact Strip -->
          <div v-if="transactions[transactionsIndex]?.length" class="animate-fade-up" style="animation-delay: 120ms">
            <div class="eco-strip">
              <div class="flex items-center gap-3">
                <div class="p-2 rounded-xl bg-emerald-500/15">
                  <CloudIcon class="h-5 w-5 text-emerald-500" />
                </div>
                <div class="flex-1">
                  <p class="text-xs font-bold text-emerald-700 dark:text-emerald-300">Sustainability Impact</p>
                  <p class="text-[10px] text-emerald-600/70 dark:text-emerald-400/70 mt-0.5">Environmental benefit from biofuel usage this period</p>
                </div>
              </div>
              <div class="grid grid-cols-3 gap-4 mt-4">
                <div class="text-center">
                  <p class="text-lg font-extrabold font-mono text-emerald-700 dark:text-emerald-300 tabular-nums">
                    {{ formatCompact(currentCo2Savings) }} Kg
                  </p>
                  <p class="text-[10px] font-medium text-emerald-600/80 dark:text-emerald-400/80 mt-0.5">CO2 Prevented</p>
                </div>
                <div class="text-center border-x border-emerald-200/40 dark:border-emerald-800/30">
                  <p class="text-lg font-extrabold font-mono text-teal-700 dark:text-teal-300 tabular-nums">
                    {{ formatNumber(currentEquivTrees) }}
                  </p>
                  <p class="text-[10px] font-medium text-teal-600/80 dark:text-teal-400/80 mt-0.5">Equiv. Trees Planted</p>
                </div>
                <div class="text-center">
                  <p class="text-lg font-extrabold font-mono text-blue-700 dark:text-blue-300 tabular-nums">
                    {{ formatNumber(currentEquivCars) }}
                  </p>
                  <p class="text-[10px] font-medium text-blue-600/80 dark:text-blue-400/80 mt-0.5">Cars Off Road Equiv.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Consumption Summary Section -->
          <div class="animate-fade-up" style="animation-delay: 140ms">
            <BaseCard class="rounded-2xl border border-surface-200/60 dark:border-surface-700/60 bg-white dark:bg-surface-900/80" elevated>
              <template #header>
                <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div class="flex items-center gap-3">
                    <div class="p-1.5 rounded-lg bg-accent-500/10">
                      <TableCellsIcon class="h-5 w-5 text-accent-500" />
                    </div>
                    <div>
                      <h2 class="text-sm font-bold text-surface-900 dark:text-surface-100">
                        {{ userProfile.group ? 'Vessel' : 'Asset' }} Consumption Summary
                      </h2>
                      <p class="text-[10px] text-surface-400 dark:text-surface-500 mt-0.5">
                        Fuel consumption breakdown by {{ userProfile.group ? 'vessel' : 'asset' }}
                      </p>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="flex flex-wrap gap-2">
                    <BaseButton
                      variant="secondary"
                      size="sm"
                      @click="navigateToAllTransactions"
                      :icon="EyeIcon"
                    >
                      View Details
                    </BaseButton>

                    <BaseButton
                      variant="primary"
                      size="sm"
                      @click="exportCardsToPDF"
                      :icon="ArrowDownTrayIcon"
                      :loading="isExporting"
                    >
                      Summary Report
                    </BaseButton>

                    <BaseButton
                      variant="accent"
                      size="sm"
                      @click="exportSummaryToCSV"
                      :icon="DocumentArrowDownIcon"
                    >
                      Export CSV
                    </BaseButton>

                    <BaseButton
                      variant="accent"
                      size="sm"
                      @click="fetchAllTransactions"
                      :icon="DocumentArrowDownIcon"
                      :loading="isExportingAll"
                    >
                      All Transactions
                    </BaseButton>
                  </div>
                </div>
              </template>

              <!-- Data Table -->
              <DataTable
                v-if="transactions[transactionsIndex]?.length"
                :headers="headers"
                :items="transactions[transactionsIndex]"
                :search-placeholder="`Search ${userProfile.group ? 'vessels' : 'assets'}`"
                :rows-per-page="10"
                :export-filename="`${userProfile.group ? 'vessel' : 'asset'}-summary.csv`"
                @row-click="handleRowClick"
              >
                <template #item-sites_display="item">
                  <div v-if="item.sites && item.sites.length" class="flex flex-wrap gap-1">
                    <span
                      v-for="s in item.sites"
                      :key="s"
                      class="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-surface-100 text-surface-700 dark:bg-surface-800 dark:text-surface-200 border border-surface-200/60 dark:border-surface-700/60"
                    >
                      {{ s }}
                    </span>
                  </div>
                  <span v-else class="text-surface-400">—</span>
                </template>

                <template #item-total_quantity_consumed="{ total_quantity_consumed }">
                  <span class="font-semibold text-surface-900 dark:text-surface-100 font-mono tabular-nums">
                    {{ formatNumberFull(total_quantity_consumed) }} L
                  </span>
                </template>

                <template #item-transaction_count="item">
                  <span class="font-mono tabular-nums text-surface-600 dark:text-surface-300">
                    {{ item.transactions ? item.transactions.length : '—' }}
                  </span>
                </template>

                <template #item-co2_saved="item">
                  <span class="font-mono tabular-nums text-emerald-600 dark:text-emerald-400 font-semibold">
                    {{ formatNumberFull(item.total_quantity_consumed * co2FactorFor()) }} Kg
                  </span>
                </template>

                <template #empty>
                  <EmptyState
                    :icon="ChartBarIcon"
                    title="No consumption data"
                    message="There is no consumption data for the selected period. Try adjusting your date range or filters."
                    variant="info"
                  />
                </template>
              </DataTable>

              <!-- Empty state when no transactions -->
              <div v-else class="py-12">
                <EmptyState
                  :icon="ChartBarIcon"
                  title="No consumption data"
                  message="There is no consumption data for the selected period. Try adjusting your date range or filters."
                  variant="info"
                />
              </div>
            </BaseCard>
          </div>
        </div>
        </template>
      </div>
    </main>

    <Footer />

    <!-- Export PDF Modal -->
    <ExportPDFModal
      v-model="showExportModal"
      :currentCustomerName="customersMcs[transactionsIndex]?.customer_name ?? ''"
      :totalCustomers="customersMcs.length"
      :hasMultipleCustomers="customersMcs.length > 1"
      :isExporting="isExporting"
      @export="handlePDFExport"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, watchEffect } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { authFetch } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { formatInTimeZone } from 'date-fns-tz'
import { savingsFactorFor } from '@/utils/emissions'

// Components
import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import Breadcrumbs from '@/components/ui/Breadcrumbs.vue'
import DateRangePicker from '@/components/dashboard/DateRangePicker.vue'
import EntityFilter from '@/components/dashboard/EntityFilter.vue'
import DataTable from '@/components/ui/DataTable.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ExportPDFModal from '@/components/ExportPDFModal.vue'
import PortalAdminCustomerPicker from '@/components/PortalAdminCustomerPicker.vue'
import { useEffectiveCustomers } from '@/composables/useEffectiveCustomers'
import { useCustomerDataRange } from '@/composables/useCustomerDataRange'

// Icons
import {
  EyeIcon,
  ArrowDownTrayIcon,
  DocumentArrowDownIcon,
  ChartBarIcon,
  TruckIcon,
  ArrowTrendingUpIcon,
  BeakerIcon,
  CloudIcon,
  TableCellsIcon,
  AdjustmentsHorizontalIcon,
  CubeIcon,
  DocumentTextIcon,
  BoltIcon
} from '@heroicons/vue/24/outline'

const store = useStore()
const router = useRouter()
const { success, error: showError } = useToast()

// PDF Generator
import { generateSummaryPDF } from '@/utils/pdfGenerator'

// State
const isLoading = ref(true)
const isExporting = ref(false)
const isExportingAll = ref(false)
const showExportModal = ref(false)
const transactions = ref([])
const transactionsIndex = ref(0)
const cardData = ref([])
const customer_ids = ref([])
const allEntities = ref([])
const runTime = ref(0)
const selectedEntities = ref([])
const allSites = ref([])
const selectedSites = ref([])

const TZ = 'Asia/Dubai'
const dateRange = ref({
  startDate: formatInTimeZone(
    new Date(new Date().getTime() - 180 * 24 * 3600 * 1000),
    TZ,
    'yyyy-MM-dd'
  ),
  endDate: formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd')
})

const userProfile = computed(() => store.state.userProfile)
const { isPortalSuperAdmin, customersMcs } = useEffectiveCustomers()

const { minDate, maxDate } = useCustomerDataRange(
  computed(() => customersMcs.value.map((c) => c.mcs_customer_id))
)

const breadcrumbItems = [
  { label: 'Dashboard' }
]

const startDate = computed(() => dateRange.value.startDate)
const endDate = computed(() => dateRange.value.endDate)

// Per-customer CO2-savings factor (kg saved / litre). Falls back to the active
// customer when no id is passed (single-customer views).
const co2FactorFor = (mcsCustomerId) => {
  const id = mcsCustomerId ?? customersMcs.value[transactionsIndex.value]?.mcs_customer_id
  return savingsFactorFor(id, customersMcs.value)
}

// Quick Stats for current account
const currentAccountStats = computed(() => {
  if (!transactions.value[transactionsIndex.value]?.length) return null

  const currentTransactions = transactions.value[transactionsIndex.value]
  const totalAssets = currentTransactions.length
  const totalVolume = currentTransactions.reduce((sum, t) => sum + t.total_quantity_consumed, 0)
  const co2Savings = totalVolume * co2FactorFor()
  const totalTxnCount = currentTransactions.reduce((sum, t) => sum + (t.transactions?.length || 0), 0)
  const avgVolumePerAsset = totalAssets > 0 ? totalVolume / totalAssets : 0

  const stats = [
    {
      label: userProfile.value.group ? 'Total Vessels' : 'Total Assets',
      value: totalAssets.toString(),
      icon: TruckIcon,
      color: '#10B981'
    },
    {
      label: 'Total Fuel Volume',
      value: `${formatCompact(totalVolume)} L`,
      icon: BeakerIcon,
      color: '#059669'
    },
    {
      label: 'CO2 Savings',
      value: `${formatCompact(co2Savings)} Kg`,
      icon: CloudIcon,
      color: '#047857'
    },
  ]

  if (!userProfile.value.group) {
    stats.push({
      label: 'Total Transactions',
      value: formatNumber(totalTxnCount),
      icon: DocumentTextIcon,
      color: '#0D9488'
    })
    stats.push({
      label: `Avg Volume / ${userProfile.value.group ? 'Vessel' : 'Asset'}`,
      value: `${formatCompact(avgVolumePerAsset)} L`,
      icon: ChartBarIcon,
      color: '#0891B2'
    })
  } else {
    stats.push({
      label: 'Total Deliveries',
      value: formatNumber(totalTxnCount),
      icon: ArrowTrendingUpIcon,
      color: '#0D9488'
    })
    stats.push({
      label: 'Avg Volume / Vessel',
      value: `${formatCompact(avgVolumePerAsset)} L`,
      icon: ChartBarIcon,
      color: '#0891B2'
    })
  }

  return stats
})

const statsGridClass = computed(() => {
  const count = currentAccountStats.value?.length || 0
  if (count >= 5) return 'grid grid-cols-2 lg:grid-cols-5 gap-4'
  if (count === 4) return 'grid grid-cols-2 lg:grid-cols-4 gap-4'
  if (count === 3) return 'grid grid-cols-1 md:grid-cols-3 gap-4'
  return 'grid grid-cols-1 md:grid-cols-2 gap-4'
})

// Environmental impact for current account
const currentCo2Savings = computed(() => {
  if (!transactions.value[transactionsIndex.value]?.length) return 0
  const totalVolume = transactions.value[transactionsIndex.value].reduce((sum, t) => sum + t.total_quantity_consumed, 0)
  return totalVolume * co2FactorFor()
})

const currentEquivTrees = computed(() => Math.round(currentCo2Savings.value * 0.05))
const currentEquivCars = computed(() => Math.round(currentCo2Savings.value * 0.0004))

// Table Headers
const headers = computed(() => {
  const baseHeaders = [
    {
      value: 'vehicle_no',
      text: userProfile.value.group ? 'Vessel Name' : 'Asset Number',
      sortable: true,
    },
    {
      value: 'sites_display',
      text: 'Sites',
    },
    {
      value: 'total_quantity_consumed',
      text: 'Quantity (L)',
      sortable: true,
    },
    {
      value: 'product_name',
      text: 'Fuel Type',
    },
    {
      value: 'transaction_count',
      text: userProfile.value.group ? 'Deliveries' : 'Transactions',
      sortable: true,
    },
    {
      value: 'co2_saved',
      text: 'CO2 Saved (Kg)',
      sortable: true,
    },
  ]

  return baseHeaders
})

// Helper Functions
const formatCompact = (num) => {
  if (typeof num !== 'number' || isNaN(num)) return '0'
  if (num >= 1000000) return (num / 1000000).toFixed(2) + ' Mil'
  else if (num >= 1000) return (num / 1000).toFixed(2) + ' K'
  return num.toFixed(2)
}

const formatNumber = (num) => {
  if (typeof num !== 'number' || isNaN(num)) return '0'
  return Math.round(num).toLocaleString()
}

const formatNumberFull = (num) => {
  return num?.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

const handleRowClick = (item) => {
  console.log('Row clicked:', item)
}

const navigateToAllTransactions = () => {
  const customer = customersMcs.value[transactionsIndex.value]
  if (customer) {
    router.push({
      name: 'AllTransactions',
      params: {
        customer_id: customer.mcs_customer_id,
      },
    })
  }
}

// Export Functions
const exportCardsToPDF = () => {
  showExportModal.value = true
}

const handlePDFExport = async (exportType) => {
  isExporting.value = true
  const includeAllCustomers = exportType === 'all'

  try {
    const formatDateForPDF = (date) => {
      const d = new Date(date)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}/${month}/${year}`
    }

    const dateRangeText = `${formatDateForPDF(startDate.value)} - ${formatDateForPDF(endDate.value)}`
    const selectedAssetsText = selectedEntities.value.length > 0
      ? selectedEntities.value.map(e => e.name)
      : 'All'

    const pdfData = {
      reportTitle: 'Fleet Summary Report',
      dateRange: dateRangeText,
      selectedAssets: selectedAssetsText,
      customers: customersMcs.value.map(c => ({ ...c, site_name: c.site })),
      cardData: cardData.value,
      includeAllCustomers,
      currentCustomerIndex: transactionsIndex.value,
      isGroupCustomer: userProfile.value.group || false
    }

    const pdf = await generateSummaryPDF(pdfData)

    const currentCustomer = customersMcs.value[transactionsIndex.value]
    const todayStr = formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd')
    const fileName = includeAllCustomers
      ? `fleet-summary-all-customers-${todayStr}.pdf`
      : `fleet-summary-${currentCustomer?.customer_name?.replace(/[^a-z0-9]/gi, '-') ?? 'customer'}-${todayStr}.pdf`

    pdf.save(fileName)

    success('PDF report generated successfully!')
    showExportModal.value = false
  } catch (err) {
    console.error('Error generating PDF:', err)
    showError('Failed to generate PDF report')
  } finally {
    isExporting.value = false
  }
}

const exportSummaryToCSV = () => {
  const csvContent = convertToCSV(transactions.value[transactionsIndex.value])
  const filename = userProfile.value.group ? 'vessel-summary.csv' : 'asset-summary.csv'
  triggerDownloadCSV(csvContent, filename)
  success('Summary exported successfully!')
}

const convertToCSV = (data) => {
  const csvHeaders = userProfile.value.group
    ? ['Vessel Name', 'Quantity (L)', 'Fuel Type', 'Deliveries', 'CO2 Saved (Kg)']
    : ['Asset Number', 'Quantity (L)', 'Fuel Type', 'Transactions', 'CO2 Saved (Kg)']

  const csvRows = [csvHeaders.join(',')]

  for (const item of data) {
    const txnCount = item.transactions?.length || 0
    const co2Saved = (item.total_quantity_consumed * co2FactorFor()).toFixed(2)
    const row = [
      item.vehicle_no,
      item.total_quantity_consumed,
      item.product_name,
      txnCount,
      co2Saved,
    ]
    csvRows.push(row.join(','))
  }

  return csvRows.join('\n')
}

const triggerDownloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.setAttribute('hidden', '')
  a.setAttribute('href', url)
  a.setAttribute('download', filename)
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

const convertAllTransactionToCSV = (data) => {
  const csvHeaders = [
    'Sale Date',
    userProfile.value.group ? 'Vessel Name' : 'Asset Number',
    userProfile.value.group ? 'Engine Hours' : 'Odometer Reading',
    'Quantity',
    'Fuel Type',
  ]

  const csvRows = [csvHeaders.join(',')]

  for (const item of data) {
    const row = [
      item.transaction_datetime,
      item.vehicle_no,
      item.odometer ?? '',
      item.quantity,
      item.product_name,
    ]
    csvRows.push(row.join(','))
  }

  return csvRows.join('\n')
}

const fetchAllTransactions = async () => {
  isExportingAll.value = true
  const apiUrl = store.state.apiUrl
  const _customer = customersMcs.value[transactionsIndex.value]

  try {
    const response = await authFetch(
      `${apiUrl}api/v1/customers/${_customer.mcs_customer_id}/transactions?start_date=${startDate.value}&end_date=${endDate.value}`,
      {},
      store.state.user
    )

    if (response.ok) {
      const result = await response.json()
      let responseData = result.data

      const csvdata = convertAllTransactionToCSV(responseData)
      const filename = userProfile.value.group
        ? 'vessel-transactions.csv'
        : 'all-transactions.csv'
      triggerDownloadCSV(csvdata, filename)

      success('Transactions exported successfully!')
    } else {
      throw new Error('Failed to fetch transactions')
    }
  } catch (error) {
    console.error('Error fetching transactions:', error)
    showError('Failed to export transactions')
  } finally {
    isExportingAll.value = false
  }
}

// Generation counter: every fetch trigger takes a new generation and only the
// newest in-flight generation may write state, so rapid customer/filter/date
// switches can't let a stale (slower) response overwrite the latest request.
let fetchGen = 0

const fetchTransactions = async (customer_ids, myGen) => {
  let accumulatedEntities = []
  const apiUrl = store.state.apiUrl

  try {
    for (let index = 0; index < customer_ids.length; index++) {
      const customer_id = customer_ids[index]
      const response = await authFetch(
        `${apiUrl}api/v1/customers/${customer_id}/volumetric?start_date=${startDate.value}&end_date=${endDate.value}`,
        {},
        store.state.user
      )
      if (myGen !== fetchGen) return

      if (response.ok) {
        const result = await response.json()
        if (myGen !== fetchGen) return
        let data = result.data

        if (selectedEntities.value.length) {
          data = data.filter((transaction) =>
            selectedEntities.value.map((e) => e.name).includes(transaction.vehicle_no)
          )
        }

        if (selectedSites.value.length) {
          const chosenSites = selectedSites.value.map((s) => s.name)
          data = data.filter((vehicle) =>
            Array.isArray(vehicle.sites) && vehicle.sites.some((s) => chosenSites.includes(s))
          )
        }

        transactions.value[index] = data
        updateCustomerCards(transactions.value[index], index)

        const currentEntities = transactions.value[index].map((t) => t.vehicle_no)
        accumulatedEntities = [...accumulatedEntities, ...currentEntities]
      } else {
        console.error('Error fetching transactions')
      }
    }

    if (runTime.value === 0) {
      allEntities.value = [...new Set(accumulatedEntities)].map((name) => ({ name }))
      // Build distinct sites list from the first fetch (all vehicles, pre-filter).
      const sitesSet = new Set()
      transactions.value.forEach((vehicles) => {
        vehicles.forEach((v) => {
          if (Array.isArray(v.sites)) v.sites.forEach((s) => s && sitesSet.add(s))
        })
      })
      allSites.value = Array.from(sitesSet).sort().map((name) => ({ name }))
      runTime.value += 1
    }
  } catch (error) {
    if (myGen !== fetchGen) return
    console.error('Error fetching transactions:', error)
    showError('Failed to load dashboard data')
  } finally {
    // Only the newest generation may clear the loading state.
    if (myGen === fetchGen) isLoading.value = false
  }
}

const updateCustomerCards = (_transactions, index) => {
  const cards = []

  // Number of Assets/Vessels
  cards.push({
    title: userProfile.value.group ? 'Number of Vessels' : 'Number of Assets',
    calloutValue: _transactions.length.toString(),
    color: '#10B981',
  })

  if (!userProfile.value.group) {
    // Total Transactions
    const totalTxn = _transactions.reduce((sum, t) => sum + (t.transactions?.length || 0), 0)
    cards.push({
      title: 'Total Transactions',
      calloutValue: totalTxn.toString(),
      color: '#059669',
    })
  } else {
    // Number of Deliveries
    const totalDeliveries = _transactions.reduce(
      (sum, t) => sum + (t.transactions?.length || 0),
      0
    )
    cards.push({
      title: 'No of Deliveries',
      calloutValue: totalDeliveries.toString(),
      color: '#059669',
    })
  }

  // Total Volume
  const totalVolume = _transactions.reduce((sum, t) => sum + t.total_quantity_consumed, 0)
  cards.push({
    title: 'Total Volume\n(L)',
    calloutValue: formatCompact(totalVolume),
    color: '#0D9488',
  })

  // CO2 Savings — use THIS customer's factor (index), not the active customer's,
  // so the per-customer cards aggregate correctly in the all-customers PDF.
  const co2Savings = totalVolume * co2FactorFor(customersMcs.value[index]?.mcs_customer_id)
  cards.push({
    title: 'CO2 Savings\n(Kg/Co2)',
    calloutValue: formatCompact(co2Savings),
    color: '#0891B2',
  })

  cardData.value[index] = cards
}

// Watchers

// When the effective customer set changes (portal admin switching customers),
// reset all per-customer UI state: stale entity/site options and selections
// from the previous customer must not filter or label the new customer's data.
// Created BEFORE the fetch-triggering watchEffect so the reset flushes first.
const customerIdsKey = computed(() =>
  customersMcs.value.map((c) => c.mcs_customer_id).join(',')
)
watch(customerIdsKey, () => {
  if (selectedEntities.value.length) selectedEntities.value = []
  if (selectedSites.value.length) selectedSites.value = []
  runTime.value = 0
  transactionsIndex.value = 0
  transactions.value = []
  cardData.value = []
})

watchEffect(() => {
  if (customersMcs.value.length > 0) {
    const customerIds = customersMcs.value.map((c) => c.mcs_customer_id)
    customer_ids.value = customerIds
    const myGen = ++fetchGen
    isLoading.value = true
    fetchTransactions(customerIds, myGen)
  } else if (userProfile.value) {
    fetchGen++ // invalidate any in-flight fetch for a de-selected customer
    isLoading.value = false
  }
})

watch([startDate, endDate], () => {
  const myGen = ++fetchGen
  isLoading.value = true
  fetchTransactions(customer_ids.value, myGen)
})
watch(selectedEntities, () => {
  const myGen = ++fetchGen
  isLoading.value = true
  fetchTransactions(customer_ids.value, myGen)
})
watch(selectedSites, () => {
  const myGen = ++fetchGen
  isLoading.value = true
  fetchTransactions(customer_ids.value, myGen)
})
</script>

<style scoped>
/* KPI Cards */
.kpi-card {
  @apply relative overflow-hidden bg-white dark:bg-surface-900/80 rounded-2xl p-4 border border-surface-200/60 dark:border-surface-700/60;
  @apply hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300;
  animation: fadeUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

/* Eco Impact Strip */
.eco-strip {
  @apply bg-gradient-to-br from-emerald-50 via-white to-teal-50 dark:from-emerald-950/30 dark:via-surface-900/80 dark:to-teal-950/30;
  @apply rounded-2xl border border-emerald-200/50 dark:border-emerald-800/30 p-5;
}

/* Tab hover */
nav button {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

nav button:hover {
  transform: translateY(-1px);
}

/* Staggered animations */
.animate-fade-up {
  animation-fill-mode: backwards;
}
</style>
